import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TrophyIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { getAwardCategories, createAwardCategory, updateAwardCategory, deleteAwardCategory } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface AwardCategory {
  id: string;
  name: string;
  description: string;
}

export default function AwardCategoriesManager() {
  const [categories, setCategories] = useState<AwardCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<AwardCategory | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await getAwardCategories();
      
      if (error) throw error;
      
      if (data) {
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching award categories:', err);
      toast.error('Failed to load award categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    try {
      if (!newCategoryName.trim()) {
        toast.error('Category name is required');
        return;
      }
      
      const { error } = await createAwardCategory({
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim()
      });
      
      if (error) throw error;
      
      toast.success('Award category added successfully');
      setNewCategoryName('');
      setNewCategoryDescription('');
      setIsAddingCategory(false);
      fetchCategories();
    } catch (err) {
      console.error('Error adding award category:', err);
      toast.error('Failed to add award category');
    }
  };

  const handleUpdateCategory = async () => {
    try {
      if (!selectedCategory) return;
      
      if (!newCategoryName.trim()) {
        toast.error('Category name is required');
        return;
      }
      
      const { error } = await updateAwardCategory(selectedCategory.id, {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim()
      });
      
      if (error) throw error;
      
      toast.success('Award category updated successfully');
      setNewCategoryName('');
      setNewCategoryDescription('');
      setIsEditingCategory(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (err) {
      console.error('Error updating award category:', err);
      toast.error('Failed to update award category');
    }
  };

  const handleEditClick = (category: AwardCategory) => {
    setSelectedCategory(category);
    setNewCategoryName(category.name);
    setNewCategoryDescription(category.description || '');
    setIsEditingCategory(true);
    setIsAddingCategory(false);
  };

  const handleDeleteClick = (category: AwardCategory) => {
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (!selectedCategory) return;
      
      const { error } = await deleteAwardCategory(selectedCategory.id);
      
      if (error) throw error;
      
      toast.success('Award category deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (err) {
      console.error('Error deleting award category:', err);
      toast.error('Failed to delete award category');
    }
  };

  const handleCancel = () => {
    setIsAddingCategory(false);
    setIsEditingCategory(false);
    setSelectedCategory(null);
    setNewCategoryName('');
    setNewCategoryDescription('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
    >
      <div 
        className="flex items-center justify-between p-6 cursor-pointer border-b border-gray-200"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-4">
          <div className="bg-golf-orange/10 p-3 rounded-xl">
            <TrophyIcon className="w-6 h-6 text-golf-orange" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-golf-gray">Award Categories</h3>
            <p className="text-sm text-gray-500">Manage special awards for players</p>
          </div>
        </div>
        <div>
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent"></div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <button
                  onClick={() => {
                    setIsAddingCategory(true);
                    setIsEditingCategory(false);
                    setSelectedCategory(null);
                    setNewCategoryName('');
                    setNewCategoryDescription('');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
                  disabled={isAddingCategory || isEditingCategory}
                >
                  <PlusIcon className="w-5 h-5" />
                  <span>Add Award Category</span>
                </button>
              </div>

              {(isAddingCategory || isEditingCategory) && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-md font-medium text-golf-gray mb-4">
                    {isEditingCategory ? 'Edit Award Category' : 'Add New Award Category'}
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                        placeholder="e.g., Longest Drive, Closest to Pin"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={newCategoryDescription}
                        onChange={(e) => setNewCategoryDescription(e.target.value)}
                        rows={3}
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                        placeholder="Describe the award criteria"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={isEditingCategory ? handleUpdateCategory : handleAddCategory}
                        className="px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
                      >
                        {isEditingCategory ? 'Update Category' : 'Add Category'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {categories.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Award Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categories.map((category) => (
                        <tr key={category.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center">
                                <TrophyIcon className="h-6 w-6 text-golf-orange" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{category.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-500">{category.description || 'No description'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEditClick(category)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                disabled={isAddingCategory || isEditingCategory}
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(category)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                disabled={isAddingCategory || isEditingCategory}
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No award categories found. Add your first category using the button above.</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={selectedCategory?.name || ''}
        message={`Are you sure you want to delete the "${selectedCategory?.name}" award category? This will remove all player assignments for this award.`}
      />
    </motion.div>
  );
}